import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useAppState } from '../../context/AppStateContext';
import { useApi } from '../../hooks/useApi';
import ClubEnrollmentModal from '../../components/teacher/ClubEnrollmentModal';
import {
    UserGroupIcon,
    CalendarIcon,
    UserPlusIcon,
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    TrashIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

// Helper components removed in favor of unified layout

const ClubSelector = ({ clubs, activeClubId, onSelect }) => (
    <div className="flex items-center border-b border-slate-200 overflow-x-auto no-scrollbar mb-8 gap-8 mt-4">
        {clubs.map((club) => {
            const isActive = activeClubId === club.id;
            return (
                <button
                    key={club.id}
                    onClick={() => onSelect(club.id)}
                    className={`flex items-center gap-2 pb-4 px-2 border-b-2 transition-all duration-300 font-black uppercase tracking-widest text-sm whitespace-nowrap ${
                        isActive 
                        ? 'border-[#00529B] text-[#00529B]' 
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                >
                    <UserGroupIcon className="w-5 h-5" strokeWidth={2.5} />
                    {club.name}
                </button>
            );
        })}
    </div>
);

const TeacherClubs = () => {
    const { user, managed_clubs, loading: appLoading, error: appError, showToast } = useAppState();
    const { get, post } = useApi();
    // queryClient was assigned a value but never used
    const [activeClubId, setActiveClubId] = useState(null);
    const [selectedTerm, setSelectedTerm] = useState(null);

    // 0. Fetch terms for filtering
    const { data: terms } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 30 * 60 * 1000,
    });

    useEffect(() => {
        if (terms?.length > 0 && !selectedTerm) {
            const active = terms.find(t => t.is_current) || terms[0];
            if (active) {
                setSelectedTerm(active.id.toString());
            }
        }
    }, [terms, selectedTerm]);
    
    // TanStack Queries
    const { 
        data: clubData = { roster: [], assignments: [], events: [] }, 
        isLoading: isQueriesLoading,
        refetch: refetchClubData 
    } = useQuery({
        queryKey: ['club-data', activeClubId, selectedTerm],
        queryFn: async () => {
            if (!activeClubId) return { roster: [], assignments: [], events: [] };
            const termParam = selectedTerm ? `&term_id=${selectedTerm}` : '';
            const [roster, assignments, events] = await Promise.all([
                get(`/api/events/clubs/${activeClubId}/roster/`),
                get(`/api/events/club-assignments/?club=${activeClubId}${termParam}`),
                get(`/api/events/clubs/${activeClubId}/events/`),
            ]);
            return { 
                roster: roster || [], 
                assignments: assignments || [], 
                events: events || [] 
            };
        },
        enabled: !!activeClubId && !!selectedTerm,
        staleTime: 5 * 60 * 1000,
        placeholderData: keepPreviousData,
    });

    const clubMembers = clubData.roster;
    const clubEvents = clubData.events;
    const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const itemsPerPage = 7;

    // Derived & Sorted Clubs
    const sortedClubs = useMemo(() => {
        if (!managed_clubs) return [];
        return [...managed_clubs].sort((a, b) => a.name.localeCompare(b.name));
    }, [managed_clubs]);

    useEffect(() => {
        if (sortedClubs.length > 0 && !activeClubId) {
            setActiveClubId(sortedClubs[0].id);
        }
    }, [sortedClubs, activeClubId]);

    const activeClub = managed_clubs?.find(c => c.id === activeClubId);

    // Derived stats
    const stats = useMemo(() => ({
        students: clubMembers.length
    }), [clubMembers]);

    // clubStats and performanceStats removed

    // performanceStats removed

    // Memoized filter and pagination
    const filteredMembers = useMemo(() => {
        if (!searchTerm) return clubMembers;
        return clubMembers.filter(member => 
            (member.student_name || member.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.student_id?.toString().includes(searchTerm) ||
            member.pathway?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [clubMembers, searchTerm]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredMembers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // handleEditAssignment and handleDeleteAssignment removed

    const handleRemoveStudent = async (student) => {
        if (!window.confirm(`Are you sure you want to remove ${student.name || student.student_name} from ${activeClub?.name}?`)) return;
        
        try {
            await post(`/api/events/clubs/${activeClubId}/remove-student/`, { student_id: student.id });
            showToast('Scholar removed successfully', 'success');
            refetchClubData(); // Refresh list
        } catch (err) {
            console.error("Failed to remove student:", err);
            showToast('Failed to remove scholar', 'error');
        }
    };


    if ((appLoading || isQueriesLoading) && (!managed_clubs || (activeClubId && clubMembers.length === 0))) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <div className="w-16 h-16 border-4 border-[#18216D]/10 border-t-[#18216D] rounded-full animate-spin"></div>
                <p className="mt-4 text-slate-400 font-bold uppercase tracking-widest text-xs">Accessing Club Registry...</p>
            </div>
        );
    }

    if (appError) {
        return (
            <div className="p-8 text-center text-rose-500 font-bold">{appError}</div>
        );
    }

    if (!managed_clubs || managed_clubs.length === 0) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
                <div className="bg-white rounded-[2.5rem] p-20 text-center border border-slate-100 shadow-xl shadow-indigo-900/5 max-w-2xl">
                    <div className="w-24 h-24 bg-slate-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6">
                        <UserGroupIcon className="w-10 h-10 text-slate-300" />
                    </div>
                    <h2 className="text-2xl font-black text-[#18216D] mb-2">No Active Patronage</h2>
                    <p className="text-slate-500 font-medium text-sm">
                        You are not currently assigned as a patron for any active student clubs.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 overflow-y-auto">

            <div className="flex-1 flex flex-col min-w-0">
                {/* Secondary header removed as per redesign */}

                {/* Main Content Area */}
                <main className="flex-1 overflow-y-auto p-4 md:p-8 scroll-smooth">


                    <div className="max-w-6xl mx-auto">
                        {/* Unified Single-Page View */}
                        <div className="space-y-8 text-left animate-in fade-in slide-in-from-bottom-4 duration-700">
                                {/* Dashboard Greeting */}
                                <div className="bg-[#18216D] p-6 md:p-8 rounded-[2rem] shadow-2xl shadow-indigo-900/20 relative overflow-hidden border border-white/5">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                                    <div className="relative z-10">
                                        <div className="flex flex-wrap items-center gap-4">
                                            <div className="px-5 py-2 bg-[#FFC425] text-[#18216D] text-[11px] font-black uppercase tracking-[0.2em] rounded-xl border border-[#FFC425]/20 italic shadow-lg shadow-amber-500/20">
                                                {activeClub?.name}
                                            </div>
                                            <span className="text-white/40 text-[10px] font-bold uppercase tracking-widest">• Internal Hub</span>
                                            
                                        </div>

                                        <h2 className="text-3xl md:text-4xl font-black text-white tracking-tighter italic leading-none mt-4">
                                            {(() => {
                                                const hour = new Date().getHours();
                                                if (hour >= 5 && hour < 12) return 'Good morning';
                                                if (hour >= 12 && hour < 17) return 'Good afternoon';
                                                if (hour >= 17 && hour < 21) return 'Good evening';
                                                return 'Welcome back';
                                            })()}, <span className="text-[#FFC425]">{user?.first_name || 'Patron'}</span>
                                        </h2>
                                    </div>
                                </div>

                                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 -mt-2">
                                    <div className="space-y-4">
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl shadow-sm flex items-center">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mr-3">Term Filter</label>
                                            <select 
                                                className="bg-transparent text-xs font-bold text-[#18216D] outline-none cursor-pointer"
                                                value={selectedTerm || ''}
                                                onChange={(e) => setSelectedTerm(e.target.value)}
                                            >
                                                {terms?.map(term => (
                                                    <option key={term.id} value={term.id}>{term.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Assessments and Performance cards removed */}
                                
                                {/* Moved Club Selector Below Greeting */}
                                {sortedClubs.length > 1 && (
                                    <ClubSelector 
                                        clubs={sortedClubs} 
                                        activeClubId={activeClubId} 
                                        onSelect={setActiveClubId} 
                                    />
                                )}

                                {/* 60/40 Layout Grid */}
                                <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 items-start">
                                    {/* Scholar Registry (Full Width) */}
                                    <div className="lg:col-span-10 bg-white rounded-[2.5rem] border border-slate-100 shadow-xl shadow-indigo-900/5 overflow-hidden">
                                        <div className="px-8 py-10 border-b border-slate-50 flex flex-col gap-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h3 className="text-xl font-black text-[#18216D] uppercase tracking-tighter italic flex items-center gap-3">
                                                        Scholar Registry
                                                        <span className="px-2.5 py-0.5 bg-[#18216D]/5 text-[#18216D] text-[10px] font-black rounded-full border border-[#18216D]/10 tracking-widest uppercase">
                                                            {stats.students} Enrolled
                                                        </span>
                                                    </h3>
                                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Official Enrollment Roster</p>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <button 
                                                        onClick={() => {
                                                            const csv = [
                                                                ['Student Name', 'Student ID', 'Pathway', 'Grade Level', 'Stream'],
                                                                ...clubMembers.map(m => [
                                                                    m.student_name || m.name || m.full_name,
                                                                    m.student_id,
                                                                    m.pathway,
                                                                    m.grade_level_name,
                                                                    m.stream_name
                                                                ])
                                                            ].map(e => e.join(",")).join("\n");
                                                            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                                                            const link = document.createElement("a");
                                                            const url = URL.createObjectURL(blob);
                                                            link.setAttribute("href", url);
                                                            link.setAttribute("download", `${activeClub?.name}_Roster.csv`);
                                                            link.style.visibility = 'hidden';
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                        }}
                                                        className="px-5 py-2.5 bg-white border border-slate-200 text-slate-400 rounded-xl hover:text-[#18216D] hover:border-[#18216D] transition-all shadow-sm group/export flex items-center gap-2"
                                                        title="Export Roster"
                                                    >
                                                        <ArrowDownTrayIcon className="w-4 h-4 group-hover/export:-translate-y-0.5 transition-transform" />
                                                        <span className="text-[10px] font-black uppercase tracking-widest">Export</span>
                                                    </button>
                                                    <button 
                                                        onClick={() => setShowEnrollmentModal(true)}
                                                        className="px-5 py-2.5 bg-[#18216D] text-white rounded-xl hover:bg-[#0D164F] transition-all shadow-lg shadow-indigo-900/20 group/btn flex items-center gap-2"
                                                        title="Enroll Students"
                                                    >
                                                        <UserPlusIcon className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                                                        <span className="text-[10px] font-black uppercase tracking-widest">Enroll</span>
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <div className="relative">
                                                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                <input 
                                                    type="text"
                                                    placeholder="Search identified scholars..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-bold text-[#18216D] focus:ring-4 focus:ring-[#18216D]/5 focus:border-[#18216D] transition-all outline-none placeholder:text-slate-300"
                                                />
                                            </div>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse">
                                                <thead>
                                                    <tr className="bg-slate-50/50">
                                                        <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-50">Scholar Name</th>
                                                        <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-50">Student ID</th>
                                                        <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-50">Pathway</th>
                                                        <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-50 text-right">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-50">
                                                    {currentItems.map((member, idx) => (
                                                        <tr key={member.id || idx} className="hover:bg-slate-50/50 transition-colors group">
                                                            <td className="px-8 py-5">
                                                                <div className="flex items-center gap-4">
                                                                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-[#18216D] font-black text-xs border border-indigo-100/50 group-hover:scale-110 transition-transform">
                                                                        {(member.student_name || member.name || member.full_name || 'S').charAt(0)}
                                                                    </div>
                                                                    <div>
                                                                        <div className="text-sm font-black text-[#18216D] italic tracking-tight leading-none">
                                                                            {member.student_name || member.name || member.full_name}
                                                                        </div>
                                                                        <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                                                                            {member.grade_level_name} {member.stream_name ? `- ${member.stream_name}` : ''}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-8 py-5">
                                                                <span className="text-[10px] font-bold text-slate-500 font-mono">#{member.student_id}</span>
                                                            </td>
                                                            <td className="px-8 py-5">
                                                                <span className="px-2.5 py-1 bg-slate-50 text-slate-500 text-[9px] font-black rounded-lg border border-slate-100 uppercase tracking-widest italic whitespace-nowrap">
                                                                    {member.pathway?.includes('Science, Technology, Engineering and Mathematics') ? 'STEM' : 
                                                                     member.pathway?.includes('Social Sciences') ? 'Social sciences' : 
                                                                     member.pathway || 'Standard'}
                                                                </span>
                                                            </td>
                                                            <td className="px-8 py-5 text-right">
                                                                <button
                                                                    onClick={() => handleRemoveStudent(member)}
                                                                    className="p-2 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                                                                    title="Remove from Club"
                                                                >
                                                                    <TrashIcon className="w-4 h-4" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {filteredMembers.length === 0 && (
                                                        <tr>
                                                            <td colSpan="4" className="px-10 py-20 text-center">
                                                                <div className="flex flex-col items-center">
                                                                    <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4 text-slate-200">
                                                                        <MagnifyingGlassIcon className="w-8 h-8" />
                                                                    </div>
                                                                    <p className="text-sm font-bold text-slate-400 uppercase tracking-widest italic">No matching records</p>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        {/* Pagination Footer */}
                                        {totalPages > 1 && (
                                            <div className="px-8 py-6 border-t border-slate-50 bg-slate-50/30 flex items-center justify-between">
                                                <div className="text-[9px] font-black text-slate-400 uppercase tracking-[0.15em]">
                                                    Page <span className="text-[#18216D]">{currentPage}</span> of <span className="text-[#18216D]">{totalPages}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                        className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#18216D] hover:border-[#18216D] disabled:opacity-50 disabled:hover:text-slate-400 disabled:hover:border-slate-200 transition-all active:scale-90 shadow-sm"
                                                    >
                                                        <ChevronLeftIcon className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === totalPages}
                                                        className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#18216D] hover:border-[#18216D] disabled:opacity-50 disabled:hover:text-slate-400 disabled:hover:border-slate-200 transition-all active:scale-90 shadow-sm"
                                                    >
                                                        <ChevronRightIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Recent Events (Full Width) */}
                                    <div className="lg:col-span-10">
                                        <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-xl shadow-indigo-900/5 overflow-hidden flex flex-col h-full min-h-[500px]">
                                            <div className="p-8 border-b border-slate-50 bg-slate-50/20">
                                                <h3 className="text-lg font-black text-[#18216D] uppercase tracking-tighter italic">Recent Events</h3>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Activities Timeline</p>
                                            </div>
                                            <div className="flex-1 p-8">
                                                {clubEvents.length > 0 ? (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                        {clubEvents.slice(0, 6).map((event, idx) => (
                                                            <div key={event.id || idx} className="flex gap-5 group">
                                                                <div className="w-12 h-12 shrink-0 rounded-2xl bg-indigo-50 flex items-center justify-center text-[#18216D] border border-indigo-100/50 shadow-sm transition-transform group-hover:scale-110">
                                                                    <CalendarIcon className="w-5 h-5" />
                                                                </div>
                                                                <div>
                                                                    <div className="text-[10px] font-black text-[#18216D] uppercase tracking-widest leading-none mb-1">
                                                                        {new Date(event.start_date || event.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                                                    </div>
                                                                    <div className="text-sm font-black text-slate-700 italic tracking-tight">{event.title}</div>
                                                                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 line-clamp-1">{event.description || 'No additional details'}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center py-12">
                                                        <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4 text-slate-200">
                                                            <CalendarIcon className="w-8 h-8" />
                                                        </div>
                                                        <p className="text-xs font-bold text-slate-300 uppercase tracking-[0.2em] italic">No active events recorded</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>

                {/* Modals removed as per redesign */}

                {showEnrollmentModal && (
                    <ClubEnrollmentModal 
                        clubId={activeClubId}
                        clubName={activeClub?.name}
                        onClose={() => setShowEnrollmentModal(false)}
                        onRefresh={() => refetchClubData()}
                    />
                )}
            </div>
        </div>
    );
};

export default TeacherClubs;
